import { NavGroup } from './docs-shell/docs-shell.component';

/**
 * Sidebar structure for the documentation hub (`/docs`).
 *
 * The sidebar has two parts:
 *   • {@link FLAT_GROUPS} — always-visible plain links (their sections are always
 *     in the DOM), scrolled to via the anchor href.
 *   • Two accordion panels — {@link GUIDE_PANEL_GROUPS} (User Guide) and
 *     {@link ARCH_GROUPS} (User Architecture) — that list every section. Their
 *     sections render only when that path is active, so clicking a leaf asks the
 *     host to switch the view first, then scrolls.
 *
 * Every id here must match a `<section id="…">` in the corresponding template.
 */

/** Always visible: intro, shared onboarding, and the Guide/Architecture chooser. */
export const FLAT_GROUPS: NavGroup[] = [
  {
    title: 'Introduction',
    items: [{ id: 'overview', label: 'About this guide' }],
  },
  {
    title: 'Initial setup',
    items: [
      { id: 'create-account', label: 'Create your account' },
      { id: 'sign-in', label: 'Sign in' },
      { id: 'access', label: 'Access code' },
      { id: 'repository', label: 'Connect a repository' },
      { id: 'workspace', label: 'Workspace & AI chat' },
    ],
  },
  {
    title: 'Deep dive',
    items: [{ id: 'choose-path', label: 'Guide & Architecture' }],
  },
];

/** The "Choose a tool" landing inside the User Guide. */
export const GUIDE_START_GROUPS: NavGroup[] = [
  {
    title: 'Start here',
    items: [{ id: 'tools', label: 'Choose a tool' }],
  },
];

/** USDM Builder sections — rendered when the USDM tool is chosen. */
export const GUIDE_USDM_GROUPS: NavGroup[] = [
  {
    title: 'USDM Builder',
    items: [
      { id: 'usdm', label: 'Build a USDM' },
      { id: 'usdm-viewer', label: 'USDM Viewer' },
    ],
  },
  {
    title: 'USDM · Help',
    items: [{ id: 'faq-usdm', label: 'FAQ' }],
  },
];

/** SDTM Builder sections — rendered when the SDTM tool is chosen. */
export const GUIDE_SDTM_GROUPS: NavGroup[] = [
  {
    title: 'SDTM Builder',
    items: [
      { id: 'sdtm-overview', label: 'What it does' },
      { id: 'requirements', label: 'Before you begin' },
      { id: 'open-sdtm', label: 'Open SDTM' },
    ],
  },
  {
    title: 'SDTM · Pipeline',
    items: [
      { id: 'studies', label: 'Studies workspace' },
      { id: 'new-study', label: 'Create a study' },
      { id: 'upload', label: 'Step 1 · Upload' },
      { id: 'trial-design', label: 'Step 2 · Trial Design' },
      { id: 'annotation', label: 'Step 3 · Annotation' },
      { id: 'spec', label: 'Step 4 · Spec review' },
      { id: 'codegen', label: 'Step 5 · Code generation' },
      { id: 'results', label: 'Step 6 · Deliverables' },
    ],
  },
  {
    title: 'SDTM · Reference',
    items: [
      { id: 'statuses', label: 'Step statuses' },
      { id: 'outputs', label: 'Every file you can download' },
      { id: 'admin', label: 'Standards Library' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    title: 'SDTM · Help',
    items: [{ id: 'faq-sdtm', label: 'FAQ' }],
  },
];

/** Full contents of the "User Guide" accordion — every guide section, in order. */
export const GUIDE_PANEL_GROUPS: NavGroup[] = [
  ...GUIDE_START_GROUPS,
  ...GUIDE_USDM_GROUPS,
  ...GUIDE_SDTM_GROUPS,
];

/** Full contents of the "User Architecture" accordion. */
export const ARCH_GROUPS: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { id: 'arch-overview', label: 'Overview' },
      { id: 'arch-platform', label: 'The platform' },
      { id: 'arch-layers', label: 'Logical architecture' },
    ],
  },
  {
    title: 'Deployment & security',
    items: [
      { id: 'arch-deployment', label: 'Deployment' },
      { id: 'arch-workspaces', label: 'Workspaces & isolation' },
      { id: 'arch-security', label: 'Security posture' },
      { id: 'arch-compliance', label: 'Compliance & standards' },
    ],
  },
  {
    title: 'The engine',
    items: [
      { id: 'arch-engine', label: 'The agentic engine' },
      { id: 'arch-mcp', label: 'MCP tool layer' },
    ],
  },
];
